/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useCallback } from 'react';
import { css, keyframes } from '@emotion/react';

import { MessageInput } from '@chatscope/chat-ui-kit-react';

import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import './ChatWidgetModal.css';
import { shallowEqual, useSelector } from 'react-redux';
import { Dialog } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { IAppState } from '../../store/rootDuck';
import { useGetMessagesGPT } from './hooks/useGetMessagesGPT';
import { useGetStartMessage } from './hooks/useGetStartMessage';
import ListMessages from '../../pages/home/gpt/listChats/components/ListMessages';
import { useFormatMessage } from '../../hooks';

const openAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const closeAnimation = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
`;

const chatWidgetStyle = (isClosing: boolean) => css`
  animation: ${isClosing ? closeAnimation : openAnimation} 0.3s ease-out;
  animation-fill-mode: forwards;
`;

export function ChatWidgetModal() {
  const fm = useFormatMessage();
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const {
    fetchMessagesGPT,
    sendMessage,
    messagesGPT,
    loadingSend,
    loadingGPT,
  } = useGetMessagesGPT();
  const { fetchStartMessage, startMessage } = useGetStartMessage();
  const me = useSelector(({ profile }: IAppState) => profile.me, shallowEqual);
  const messages = messagesGPT.length ? messagesGPT : startMessage || [];

  useEffect(() => {
    if (me && isOpen) {
      fetchMessagesGPT(me.id);
      fetchStartMessage();
    }
  }, [me, isOpen]);

  useEffect(() => {
    if (!isOpen && isClosing) {
      const timer = setTimeout(() => setIsClosing(false), 300); // 300ms to match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, isClosing]);

  const handleToggle = () => {
    if (isOpen) {
      setIsClosing(true);
      setIsOpen(false);
      // setTimeout(() => setIsOpen(false), 200);
    } else {
      setIsOpen(true);
    }
  };
  const handleSendMessage = useCallback(
    (message: string) => {
      sendMessage(message);
    },
    [sendMessage]
  );

  if (!me) return <></>;

  return (
    <>
      {!isOpen && !isClosing && (
        <div className='chat-widget-button-container'>
          <button
            style={{ fontSize: 15 }}
            type='button'
            className='chat-button'
            onClick={handleToggle}
          >
            AI
          </button>
        </div>
      )}
      <Dialog
        open={isOpen || isClosing}
        onClose={handleToggle}
        aria-labelledby='scroll-dialog-title'
        aria-describedby='scroll-dialog-description'
        hideBackdrop
        disableEscapeKeyDown
        PaperProps={{
          style: {
            position: 'fixed',
            right: 0,
            bottom: 0,
            margin: 0,
            maxWidth: 'none',
            maxHeight: 'none',
            width: 'auto',
            height: 'auto',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <div className='chat-widget-container' style={{ position: 'fixed', right: 0, bottom: 0 }}>
          {(isOpen || isClosing) && (
            // eslint-disable-next-line react/no-unknown-property
            <div css={chatWidgetStyle(isClosing)}>
              {isOpen && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type='button' className='chat-toggle-button' onClick={handleToggle}>
                    <CloseIcon />
                  </button>
                </div>
              )}
              <div className='chat-widget'>
                <ListMessages
                  currentChat={null}
                  loadingMessages={loadingGPT}
                  profileMe={me}
                  messagesGPT={messages}
                  isGPT
                  isFull
                  hideName
                  title={fm('CHATGPT.TITLE')}
                  cardStyles={{ height: 571 }}
                  loadingSendGPT={loadingSend}
                />
                <div style={{ padding: 10 }}>
                  <MessageInput
                    style={{
                      paddingBottom: 10,
                      paddingTop: 10,
                      fontSize: 14,
                      color: '#000',
                      fontWeight: 400,
                    }}
                    placeholder={fm('CHATGPT.INPUT')}
                    value={message}
                    onChange={(_innerHtml, _textContent, innerText) => setMessage(innerText)}
                    onSend={(_innerHtml, _textContent, innerText) => {
                      handleSendMessage(innerText);
                      setMessage('');
                    }}
                    attachButton={false}
                    sendDisabled={!message.trim()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
